import { useQuery } from '@tanstack/react-query';
import { ClientError } from 'graphql-request';
import { fetchGql } from '@/lib/graphql_client';
import { authFetch, AuthHttpError } from '@/lib/rest_client';
import { keycloak } from '@/lib/keycloak';
import { MyContextsDocument } from '@/gql/graphql';
import { normalizePersona } from './normalize-persona';
import type { ResolvedMembership } from './session.types';

/** Query key for the user's contexts; `q` is the (optional) ES search term. */
export const myContextsKey = (q?: string) => ['contexts', 'mine', q ?? ''] as const;

type RestContextRow = {
  contextTenantId: string;
  persona: string;
  partyId: string;
  platformUserId?: string;
  status?: string;
  displayName?: string | null;
};

const isGqlValidationError = (err: unknown): boolean =>
  err instanceof ClientError &&
  (err.response?.errors?.some((e) => e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') ?? false);

/**
 * Fetch the contexts (memberships) the user may act in — `myContexts` GraphQL
 * query (ENT-88 R3, ES-backed). Falls back to REST `/auth/contexts` when the
 * GraphQL field is not yet available on the backend.
 *
 * Personas are normalized to the FE vocabulary at this boundary
 * (PLATFORM_ADMIN → SA). `ContextModel` carries no `status`, so every row is
 * mapped to `status: 'ACTIVE'`. The result feeds the {@link ./context-store}
 * via the consumer (ContextSwitcher / AuthProvider).
 *
 * Pass `q` for server-side search; when omitted the query returns the caller's
 * full set of memberships (and the empty-`q` cache is shared with that call).
 */
export function useMyContexts(q?: string, opts?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: myContextsKey(q),
    queryFn: async (): Promise<ResolvedMembership[]> => {
      try {
        const { myContexts } = await fetchGql(MyContextsDocument, { q });
        return myContexts.map((r) => ({
          contextTenantId: r.tenantId,
          persona: normalizePersona(r.persona),
          partyId: r.partyId,
          displayName: r.displayName ?? null,
          status: 'ACTIVE',
        }));
      } catch (err) {
        // Backend hasn't shipped myContexts yet — fall back to REST /auth/contexts
        if (!isGqlValidationError(err)) throw err;

        try {
          const rows = await authFetch<RestContextRow[]>('/auth/contexts');
          return rows.map((r) => ({
            contextTenantId: r.contextTenantId,
            persona: normalizePersona(r.persona),
            partyId: r.partyId,
            displayName: r.displayName ?? null,
            status: 'ACTIVE',
          }));
        } catch (restErr) {
          // REST also unavailable — derive a single context from KC token claims.
          // The backend uses the JWT tenant_id for routing, so this restores the
          // pre-ENT-88 behavior while the backend ships the session-plane API.
          if (restErr instanceof AuthHttpError && restErr.status !== 404) throw restErr;
          const parsed = keycloak.tokenParsed as Record<string, unknown> | undefined;
          const tenantId = parsed?.tenant_id as string | undefined;
          if (!tenantId) throw restErr;
          const roles = ((parsed?.realm_access as { roles?: string[] } | undefined)?.roles ?? []);
          const personaRole = roles.find((r) =>
            ['PLATFORM_ADMIN', 'AGENCY', 'CLIENT', 'VENDOR', 'WORKER'].includes(r),
          );
          return [{ contextTenantId: tenantId, persona: normalizePersona(personaRole ?? 'SA'), partyId: '', displayName: null, status: 'ACTIVE' }];
        }
      }
    },
    staleTime: 5 * 60_000,
    refetchInterval: opts?.refetchInterval,
  });
}
